import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

// Pages can choose a suitable initial view without changing subscriber behavior.
const SportsPreviewContext = createContext(false);
export const useSportsPreview = () => useContext(SportsPreviewContext);

/**
 * Visual preview only: children still execute and fetch their normal data.
 * Never use this component as a replacement for server-side authorization.
 *
 * Optional page hints:
 * data-preview-target on the MAIN table or its containing section.
 * data-preview-target="section" on a chart/card that should be previewed.
 */
export default function SportsPagePreview({
  children,
  pathname,
  title,
  description,
  actions,
  authed,
}) {
  const contentRef = useRef(null);
  const viewportRef = useRef(null);
  const [frame, setFrame] = useState({ start: 0, height: 540, ready: false });

  useEffect(() => {
    const content = contentRef.current;
    const viewport = viewportRef.current;
    if (!content || !viewport) return undefined;

    // Native inert also removes descendants from keyboard and accessibility focus.
    content.inert = true;
    let animationFrame = 0;
    let stopped = false;
    const visible = (element) => element && element.getClientRects().length > 0;
    const schedule = () => {
      if (!stopped && !animationFrame) animationFrame = requestAnimationFrame(measure);
    };
    const measure = () => {
      animationFrame = 0;
      const origin = content.getBoundingClientRect().top;
      const hint = Array.from(content.querySelectorAll("[data-preview-target]")).find(visible);
      const isSection = hint?.getAttribute("data-preview-target") === "section";
      const table = isSection ? null :
        (hint?.matches("table") ? hint : hint?.querySelector("table")) ||
        Array.from(content.querySelectorAll("table")).find((element) =>
          visible(element) && element.tBodies[0]?.rows.length > 0
        );
      let end = Math.min(content.scrollHeight || 540, 540);
      let targetTop = 0;
      if (table && visible(table)) {
        // Direct body rows avoid accidentally counting nested player tables.
        const rows = Array.from(table.tBodies[0]?.rows || []).filter(visible);
        const last = rows[Math.min(2, rows.length - 1)];
        if (last) {
          targetTop = table.getBoundingClientRect().top - origin;
          end = last.getBoundingClientRect().bottom - origin;
        }
      } else if (hint && visible(hint)) {
        targetTop = hint.getBoundingClientRect().top - origin;
        end = Math.min(hint.getBoundingClientRect().bottom - origin, targetTop + 480);
      }
      // Avoid showing a long wall of optimizer settings before the actual pool.
      const start = Math.max(0, targetTop - 140, end - 720);
      const height = Math.max(160, Math.min(720, end - start));
      viewport.scrollTop = start;
      setFrame((old) => old.start === start && old.height === height && old.ready
        ? old : { start, height, ready: true });
    };
    const mutations = new MutationObserver(schedule);
    mutations.observe(content, { subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ["class", "hidden", "open"] });
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    resize?.observe(content);
    window.addEventListener("resize", schedule);
    content.addEventListener("load", schedule, true);
    schedule();
    return () => {
      stopped = true;
      cancelAnimationFrame(animationFrame);
      mutations.disconnect();
      resize?.disconnect();
      window.removeEventListener("resize", schedule);
      content.removeEventListener("load", schedule, true);
    };
  }, [pathname]);

  // Restore the measured crop if React changes its height after measuring.
  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = frame.start;
  }, [frame]);

  return (
    <section className="sports-preview">
      <style>{`
        .sports-preview{--preview-bg:#080d1b;--preview-text:#f1f5f9;--preview-muted:#a8b6cc;position:relative;z-index:0;isolation:isolate;background:var(--preview-bg);color:var(--preview-text);padding-bottom:16px;color-scheme:dark}
        .sports-preview-banner{padding:14px 20px;border-bottom:1px solid #64748b33;display:flex;align-items:center;justify-content:space-between;gap:12px;font-size:13px}
        .sports-preview-banner strong{font-weight:750}
        .sports-preview-banner span{color:var(--preview-muted)}
        .sports-preview-stage{position:relative;z-index:0;isolation:isolate;overflow:hidden}
        .sports-preview-window{position:relative;overflow:hidden;isolation:isolate;overflow-anchor:none}
        .sports-preview-content{pointer-events:none;user-select:none;-webkit-user-select:none;overflow:hidden;display:flow-root}
        .sports-preview-content *{pointer-events:none!important;user-select:none!important;-webkit-user-select:none!important}
        .sports-preview-fade{position:absolute;inset:0;z-index:2;background:linear-gradient(to bottom,transparent 50%,var(--preview-bg) 100%);cursor:default}
        .sports-preview-offer{position:relative;z-index:1;max-width:740px;margin:0 auto;padding:12px 20px 24px;text-align:center}
        .sports-preview-offer h2{font-size:clamp(21px,3vw,29px);font-weight:800;line-height:1.25;margin:0 0 12px}
        .sports-preview-offer p{color:var(--preview-muted);font-size:14px;line-height:1.6;margin:0 0 20px}
        .sports-preview-actions{display:flex;flex-wrap:wrap;justify-content:center;gap:10px}
        .sports-preview-actions a{display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:10px 17px;border:1px solid #64748b55;border-radius:9px;text-decoration:none;color:var(--preview-text);font-size:14px;font-weight:700;background:var(--preview-bg)}
        .sports-preview-actions a:first-child{background:#2563eb;color:#fff;border-color:#2563eb}
        .sports-preview-actions a:focus-visible{outline:3px solid #60a5fa;outline-offset:3px}
        @media(max-width:600px){.sports-preview-banner{align-items:flex-start;flex-direction:column;gap:4px}.sports-preview-actions a{flex:1 1 180px}}
      `}</style>
      <div className="sports-preview-banner">
        <strong>Live preview</strong>
        <span>Unlock full access to use this tool.</span>
      </div>
      <div className="sports-preview-stage">
        <div ref={viewportRef} className="sports-preview-window"
          style={{ height: frame.height }}>
          <div ref={contentRef} className="sports-preview-content" inert="" aria-hidden="true"
            style={{ visibility: frame.ready ? "visible" : "hidden" }}>
            <SportsPreviewContext.Provider value={true}>
              {children}
            </SportsPreviewContext.Provider>
          </div>
        </div>
        {/* Outside the scroll window, so the shield never scrolls out of view. */}
        <div className="sports-preview-fade" aria-hidden="true"
          onPointerDown={(event) => event.preventDefault()}
          onContextMenu={(event) => event.preventDefault()} />
      </div>
      <div className="sports-preview-offer">
        <h2>{title}</h2>
        <p>{description}</p>
        <div className="sports-preview-actions">
          {actions.map((action) => <Link key={action.href} to={action.href}>{action.label}</Link>)}
          {!authed && <Link to={`/login?from=${encodeURIComponent(pathname)}`}>Already subscribed? Log in</Link>}
        </div>
      </div>
    </section>
  );
}
